import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import AdminLogoutButton from './AdminLogoutButton';
import { collection, query, orderBy, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import './Navbar.css';

const Navbar = () => {
    const { currentUser } = useAuth();
    const { t, i18n } = useTranslation();
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [roomsList, setRoomsList] = useState([]);
    const [servicesList, setServicesList] = useState([]);
    const [experiencesList, setExperiencesList] = useState([]);
    const [isRoomsSubmenuOpen, setIsRoomsSubmenuOpen] = useState(false);
    const [isServicesSubmenuOpen, setIsServicesSubmenuOpen] = useState(false);
    const [isExperiencesSubmenuOpen, setIsExperiencesSubmenuOpen] = useState(false);
    const [siteSettings, setSiteSettings] = useState({ siteName: "L'antico Piol De Feltre", navbarLinks: [] });
    const [settingsLoading, setSettingsLoading] = useState(true);

    useEffect(() => {
        const settingsDocRef = doc(db, 'site_settings', 'global');
        const unsubscribeSettings = onSnapshot(settingsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const sortedLinks = (data.navbarLinks || []).sort((a, b) => a.order - b.order);
                setSiteSettings({ ...data, navbarLinks: sortedLinks });
            } else { console.log("Documento impostazioni globali non trovato."); }
            setSettingsLoading(false);
        }, (error) => {
            console.error("Errore listener impostazioni:", error);
            setSettingsLoading(false);
        });
        return () => unsubscribeSettings();
    }, []);

    useEffect(() => {
        const roomsQuery = query(collection(db, 'rooms'), orderBy('name.it'));
        const unsub = onSnapshot(roomsQuery, snap => setRoomsList(snap.docs.map(d => ({ id: d.id, ...d.data() }))), console.error);
        return () => unsub();
    }, []);
    useEffect(() => {
        const servicesQuery = query(collection(db, 'services'), orderBy('order', 'asc'), orderBy('title.it', 'asc'));
        const unsub = onSnapshot(servicesQuery, snap => setServicesList(snap.docs.map(d => ({ id: d.id, ...d.data() }))), console.error);
        return () => unsub();
    }, []);
    useEffect(() => {
        const experiencesQuery = query(collection(db, 'experiences'), orderBy('order', 'asc'), orderBy('title.it', 'asc'));
        const unsub = onSnapshot(experiencesQuery, snap => setExperiencesList(snap.docs.map(d => ({ id: d.id, ...d.data() }))), console.error);
        return () => unsub();
    }, []);
    useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
    useEffect(() => { document.body.classList.toggle('mobile-nav-open', isMobileNavOpen); return () => document.body.classList.remove('mobile-nav-open'); }, [isMobileNavOpen]);
    const toggleMobileNav = () => { const n = !isMobileNavOpen; setIsMobileNavOpen(n); if (!n) { setIsRoomsSubmenuOpen(false); setIsServicesSubmenuOpen(false); setIsExperiencesSubmenuOpen(false); } };
    const closeMobileNav = () => { setIsMobileNavOpen(false); setIsRoomsSubmenuOpen(false); setIsServicesSubmenuOpen(false); setIsExperiencesSubmenuOpen(false); };
    const location = useLocation();
    useEffect(() => { closeMobileNav(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggleRoomsSubmenuMobile = (e) => { e.stopPropagation(); setIsRoomsSubmenuOpen(p => !p); setIsServicesSubmenuOpen(false); setIsExperiencesSubmenuOpen(false); };
    const toggleServicesSubmenuMobile = (e) => { e.stopPropagation(); setIsServicesSubmenuOpen(p => !p); setIsRoomsSubmenuOpen(false); setIsExperiencesSubmenuOpen(false); };
    const toggleExperiencesSubmenuMobile = (e) => { e.stopPropagation(); setIsExperiencesSubmenuOpen(p => !p); setIsRoomsSubmenuOpen(false); setIsServicesSubmenuOpen(false); };
    const handleAnyLinkClick = () => { closeMobileNav(); }

    if (settingsLoading) { return <nav className="main-navbar"></nav>; }
    const renderSubmenuContent = (linkType) => {
        const lang = i18n.language; const fallbackLang = 'it';
        if (linkType === 'submenu_rooms' && roomsList.length > 0) { return roomsList.map(item => (<Link key={item.id} to={`/camere/${item.id}`} className="submenu-link" onClick={handleAnyLinkClick}>{item.name?.[lang] || item.name?.[fallbackLang] || item.name}</Link>)); }
        if (linkType === 'submenu_services' && servicesList.length > 0) { return servicesList.map(item => (<Link key={item.id} to={`/servizi/${item.id}`} className="submenu-link" onClick={handleAnyLinkClick}>{item.title?.[lang] || item.title?.[fallbackLang] || item.title}</Link>)); }
        if (linkType === 'submenu_experiences' && experiencesList.length > 0) { return experiencesList.map(item => (<Link key={item.id} to={`/esperienze/${item.id}`} className="submenu-link" onClick={handleAnyLinkClick}>{item.title?.[lang] || item.title?.[fallbackLang] || item.title}</Link>)); }
        return null;
    };

    return (
        <nav className={`main-navbar ${isScrolled ? 'scrolled' : ''} ${isMobileNavOpen ? 'mobile-open' : ''}`}>
            <div className="navbar-container container">
                <Link to="/" className="navbar-logo" onClick={handleAnyLinkClick}>{siteSettings.siteName || "Nome Sito"}</Link>
                <div className={`navbar-links ${isMobileNavOpen ? 'open' : ''}`}>
                    {(siteSettings.navbarLinks || [])
                        .filter(link => link.enabled && link.showInHeader !== false) // <-- CORREZIONE QUI
                        .map(link => {
                        const translationKeyMap = {
                             home: 'navHome',
                             camere: 'navRooms',
                             servizi: 'navServices',
                             esperienze: 'navExperiences',
                             galleria: 'navGallery',
                             posizione: 'navLocation',
                             contatti: 'navContact',
                             'external-map': 'navExternalMap',
                             'privacy-policy': 'navPrivacyPolicy',
                             'termini-e-condizioni': 'navTermsConditions'
                        };
                        const translationKey = translationKeyMap[link.id];
                        let label = link.label;
                        if (link.translations && link.translations[i18n.language] && link.translations[i18n.language].trim() !== '') {
                            label = link.translations[i18n.language];
                        } else if (translationKey) {
                            label = t(translationKey);
                        }
                        
                        if (link.type === 'external_link') {
                            return (
                                <a key={link.id} href={link.path} className="nav-link external-link" target="_blank" rel="noopener noreferrer" onClick={handleAnyLinkClick}>{label}</a>
                            );
                        }
                        
                        const hasSubmenu = (link.type === 'submenu_rooms' && roomsList.length > 0) || (link.type === 'submenu_services' && servicesList.length > 0) || (link.type === 'submenu_experiences' && experiencesList.length > 0);
                        let isSubmenuCurrentlyOpen = false; let toggleFunction = () => {};
                        if (link.type === 'submenu_rooms') { isSubmenuCurrentlyOpen = isRoomsSubmenuOpen; toggleFunction = toggleRoomsSubmenuMobile; }
                        else if (link.type === 'submenu_services') { isSubmenuCurrentlyOpen = isServicesSubmenuOpen; toggleFunction = toggleServicesSubmenuMobile; }
                        else if (link.type === 'submenu_experiences') { isSubmenuCurrentlyOpen = isExperiencesSubmenuOpen; toggleFunction = toggleExperiencesSubmenuMobile; }
                        
                        if (hasSubmenu) {
                            return (
                                <div key={link.id} className={`nav-item-with-submenu ${isSubmenuCurrentlyOpen ? 'submenu-open' : ''}`}>
                                    <div className="nav-link-container">
                                        <NavLink to={link.path} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} onClick={handleAnyLinkClick}>{label}</NavLink>
                                        <span className="submenu-indicator" onClick={toggleFunction}>{isSubmenuCurrentlyOpen ? '▲' : '►'}</span>
                                    </div>
                                    <div className="navbar-submenu">{renderSubmenuContent(link.type)}</div>
                                </div>
                            );
                        } else {
                            return (
                                <NavLink key={link.id} to={link.path} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} onClick={handleAnyLinkClick}>{label}</NavLink>
                            );
                        }
                    })}

                    <LanguageSelector onLanguageChange={closeMobileNav} />
                    {currentUser && (
                        <div className="navbar-admin-desktop-only">
                             <NavLink to="/admin-impostazioni-sito" className="nav-link admin-settings-link" title="Impostazioni Sito" onClick={handleAnyLinkClick}>⚙️</NavLink>
                             <AdminLogoutButton onLogout={handleAnyLinkClick} />
                        </div>
                    )}
                </div>
                <div className="navbar-mobile-toggle" onClick={toggleMobileNav}>
                    <div className={`hamburger-icon ${isMobileNavOpen ? 'open' : ''}`}><span></span><span></span><span></span></div>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;